import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import moment from 'moment-timezone';
import { useAuth } from '../context/AuthContext';
import './Admin.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const APP_TIMEZONE = process.env.REACT_APP_TIMEZONE || 'America/New_York';

const Admin = () => {
  const { token } = useAuth();
  
  // Helper function to check if PTO can be deleted
  const canDeletePTO = (pto) => {
    // Cannot delete approved PTOs that are in the past (already used)
    const ptoDate = moment.tz(pto.date, APP_TIMEZONE);
    const today = moment.tz(APP_TIMEZONE).startOf('day');
    const isPast = ptoDate.isBefore(today);
    
    if (pto.status === 'approved' && isPast) {
      return false;
    }
    
    return true;
  };

  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailConfig, setEmailConfig] = useState({
    host: 'smtp.gmail.com',
    port: 587,
    secure: false,
    user: '',
    password: '',
    from: '',
    isActive: false
  });
  const [emailLoading, setEmailLoading] = useState(true);
  const [savingEmail, setSavingEmail] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [backups, setBackups] = useState([]);
  const [backupLoading, setBackupLoading] = useState(true);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [restoringFromFile, setRestoringFromFile] = useState(false);
  const [backupConfig, setBackupConfig] = useState({
    schedule: '0 2 * * *',
    timezone: 'America/New_York',
    isEnabled: true,
    keepCount: 30
  });
  const [backupConfigLoading, setBackupConfigLoading] = useState(true);
  const [savingBackupConfig, setSavingBackupConfig] = useState(false);
  const [sendEmailModal, setSendEmailModal] = useState({ show: false, fileName: null, to: '' });
  const [sendingBackupEmail, setSendingBackupEmail] = useState(false);
  const [scheduleUI, setScheduleUI] = useState({
    frequency: 'daily', // 'daily', 'weekly', 'monthly'
    hour: 2,
    minute: 0,
    dayOfWeek: 0, // 0-6 (Sunday-Saturday)
    dayOfMonth: 1 // 1-31
  });
  const [ptoRequests, setPTORequests] = useState([]);
  const [ptoLoading, setPTOLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('all');

  // Parse cron schedule to UI format
  const parseCronToUI = (cronSchedule) => {
    if (cronSchedule) {
      const parts = cronSchedule.trim().split(/\s+/);
      if (parts.length === 5) {
        const [minute, hour, dayOfMonth, , dayOfWeek] = parts;
        
        let frequency = 'daily';
        if (dayOfWeek !== '*') {
          frequency = 'weekly';
        } else if (dayOfMonth !== '*') {
          frequency = 'monthly';
        }
        
        return {
          frequency,
          hour: parseInt(hour) || 2,
          minute: parseInt(minute) || 0,
          dayOfWeek: dayOfWeek !== '*' ? parseInt(dayOfWeek) : 0,
          dayOfMonth: dayOfMonth !== '*' ? parseInt(dayOfMonth) : 1
        };
      }
    }
    return {
      frequency: 'daily',
      hour: 2,
      minute: 0,
      dayOfWeek: 0,
      dayOfMonth: 1
    };
  };

  const fetchBackupConfig = useCallback(async () => {
    try {
      setBackupConfigLoading(true);
      const response = await axios.get(`${API_URL}/backup/config`);
      if (response.data.config) {
        setBackupConfig(response.data.config);
        // Parse cron schedule to UI format
        setScheduleUI(parseCronToUI(response.data.config.schedule));
      }
    } catch (error) {
      console.error('Error fetching backup config:', error);
    } finally {
      setBackupConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReminders();
    fetchEmailConfig();
    fetchBackups();
    fetchBackupConfig();
    fetchPTORequests();
    fetchUsers();
  }, [fetchBackupConfig]);

  const fetchReminders = async () => {
    try {
      const response = await axios.get(`${API_URL}/reminders`);
      setReminders(response.data.reminders);
    } catch (error) {
      console.error('Error fetching reminders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (id, isEnabled) => {
    try {
      await axios.put(`${API_URL}/reminders/${id}`, { isEnabled: !isEnabled });
      fetchReminders();
    } catch (error) {
      console.error('Error updating reminder:', error);
      alert('Failed to update reminder');
    }
  };

  const handleScheduleUpdate = async (id, field, value) => {
    try {
      await axios.put(`${API_URL}/reminders/${id}`, {
        schedule: { [field]: value }
      });
      fetchReminders();
    } catch (error) {
      console.error('Error updating reminder schedule:', error);
      alert('Failed to update reminder schedule');
    }
  };

  const handleTrigger = async (id, type) => {
    try {
      await axios.post(`${API_URL}/reminders/${id}/trigger`);
      alert('Reminder triggered successfully!');
      fetchReminders();
    } catch (error) {
      console.error('Error triggering reminder:', error);
      alert('Failed to trigger reminder');
    }
  };

  const fetchEmailConfig = async () => {
    try {
      setEmailLoading(true);
      const response = await axios.get(`${API_URL}/email-config`);
      if (response.data.config) {
        setEmailConfig(response.data.config);
      }
    } catch (error) {
      console.error('Error fetching email config:', error);
    } finally {
      setEmailLoading(false);
    }
  };

  const handleEmailConfigChange = (field, value) => {
    setEmailConfig(prev => ({
      ...prev,
      [field]: field === 'port' ? parseInt(value) || 587 : 
               field === 'secure' ? value === 'true' || value === true :
               value
    }));
  };

  const handleSaveEmailConfig = async (e) => {
    e.preventDefault();
    try {
      setSavingEmail(true);
      await axios.put(`${API_URL}/email-config`, emailConfig);
      alert('Email configuration saved successfully!');
      await fetchEmailConfig();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save email configuration');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleTestEmail = async () => {
    try {
      setTestingEmail(true);
      await axios.post(`${API_URL}/email-config/test`);
      alert('Test email sent successfully! Check your inbox.');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  const fetchBackups = async () => {
    try {
      setBackupLoading(true);
      const response = await axios.get(`${API_URL}/backup/list`);
      setBackups(response.data.backups);
    } catch (error) {
      console.error('Error fetching backups:', error);
    } finally {
      setBackupLoading(false);
    }
  };

  const fetchPTORequests = async () => {
    try {
      setPTOLoading(true);
      const currentYear = moment().year();
      const response = await axios.get(`${API_URL}/pto`, {
        params: { year: currentYear }
      });
      setPTORequests(response.data.ptos);
    } catch (error) {
      console.error('Error fetching PTO requests:', error);
    } finally {
      setPTOLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users`);
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handlePTOStatusChange = async (ptoId, status) => {
    try {
      await axios.put(`${API_URL}/pto/${ptoId}`, { status });
      alert(`PTO ${status} successfully!`);
      fetchPTORequests();
    } catch (error) {
      alert(error.response?.data?.message || `Failed to ${status} PTO`);
    }
  };

  const handlePTODelete = async (ptoId) => {
    if (!window.confirm('Are you sure you want to delete this PTO request?')) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/pto/${ptoId}`);
      alert('PTO deleted successfully!');
      fetchPTORequests();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete PTO');
    }
  };

  const handleCreateBackup = async () => {
    if (!window.confirm('Create a new backup? This may take a few moments.')) {
      return;
    }
    
    try {
      setCreatingBackup(true);
      await axios.post(`${API_URL}/backup/create`);
      alert('Backup created successfully!');
      await fetchBackups();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to create backup');
    } finally {
      setCreatingBackup(false);
    }
  };

  const handleRestoreFromFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    if (!file.name.endsWith('.zip')) {
      alert('Please select a valid .zip backup file.');
      return;
    }

    if (!window.confirm(`Restore from file "${file.name}"?\n\nThis will overwrite all current data with the backup data. This action cannot be undone!`)) {
      return;
    }

    try {
      setRestoringFromFile(true);
      const formData = new FormData();
      formData.append('backupFile', file);
      const response = await axios.post(`${API_URL}/backup/restore-file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert('Backup restored successfully!\n' + (response.data.details || response.data.message));
      await fetchBackups();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to restore backup from file');
    } finally {
      setRestoringFromFile(false);
    }
  };

  const handleDownloadBackup = (fileName) => {
    const encodedToken = encodeURIComponent(token);
    const url = `${API_URL}/backup/download/${fileName}?token=${encodedToken}`;
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleDeleteBackup = async (fileName) => {
    if (!window.confirm(`Are you sure you want to delete backup "${fileName}"?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/backup/${fileName}`);
      alert('Backup deleted successfully!');
      await fetchBackups();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete backup');
    }
  };

  const handleRestoreBackup = async (fileName) => {
    if (!window.confirm(`Are you sure you want to restore from backup "${fileName}"?\n\nThis will overwrite all current data with the backup data. This action cannot be undone!`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/backup/restore/${fileName}`);
      alert('Backup restored successfully!\n' + response.data.message);
      await fetchBackups();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to restore backup');
    }
  };

  const handleOpenSendEmailModal = (fileName) => {
    setSendEmailModal({
      show: true,
      fileName,
      to: emailConfig.user || ''
    });
  };

  const handleCloseSendEmailModal = () => {
    setSendEmailModal({ show: false, fileName: null, to: '' });
  };

  const handleSendBackupEmail = async (e) => {
    e.preventDefault();
    if (!sendEmailModal.fileName || !sendEmailModal.to.trim()) {
      alert('Please enter a recipient email address.');
      return;
    }
    try {
      setSendingBackupEmail(true);
      await axios.post(`${API_URL}/backup/send-email/${encodeURIComponent(sendEmailModal.fileName)}`, {
        to: sendEmailModal.to.trim()
      });
      alert('Backup file sent via email successfully!');
      handleCloseSendEmailModal();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to send backup via email');
    } finally {
      setSendingBackupEmail(false);
    }
  };

  // Convert UI selections to cron schedule
  const convertUIToCron = (ui) => {
    let cron = '';
    if (ui.frequency === 'daily') {
      cron = `${ui.minute} ${ui.hour} * * *`;
    } else if (ui.frequency === 'weekly') {
      cron = `${ui.minute} ${ui.hour} * * ${ui.dayOfWeek}`;
    } else if (ui.frequency === 'monthly') {
      cron = `${ui.minute} ${ui.hour} ${ui.dayOfMonth} * *`;
    }
    return cron;
  };


  const handleBackupConfigChange = (field, value) => {
    setBackupConfig(prev => ({
      ...prev,
      [field]: field === 'keepCount' ? parseInt(value) || 30 :
               field === 'isEnabled' ? value === 'true' || value === true :
               value
    }));
  };

  const handleScheduleUIChange = (field, value) => {
    setScheduleUI(prev => {
      const updated = {
        ...prev,
        [field]: field === 'hour' || field === 'minute' || field === 'dayOfWeek' || field === 'dayOfMonth' 
          ? parseInt(value) || 0 
          : value
      };
      
      // Update cron schedule when UI changes
      const cronSchedule = convertUIToCron(updated);
      setBackupConfig(prevConfig => ({
        ...prevConfig,
        schedule: cronSchedule
      }));
      
      return updated;
    });
  };

  const handleSaveBackupConfig = async (e) => {
    e.preventDefault();
    try {
      setSavingBackupConfig(true);
      // Ensure schedule is up to date
      const cronSchedule = convertUIToCron(scheduleUI);
      await axios.put(`${API_URL}/backup/config`, {
        ...backupConfig,
        schedule: cronSchedule
      });
      alert('Backup configuration saved successfully!');
      await fetchBackupConfig();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save backup configuration');
    } finally {
      setSavingBackupConfig(false);
    }
  };

  const formatSchedule = (reminder) => {
    if (!reminder.schedule) return 'Not scheduled';
    const { day, time } = reminder.schedule;
    const dayName = day ? day.charAt(0).toUpperCase() + day.slice(1) : '';
    return `${dayName} ${time || ''} EST`;
  };

  return (
    <Layout>
      <div className="admin-page">
        <h1>Admin Dashboard</h1>

        <div className="email-config-section">
          <h2>Email Configuration</h2>
          <p className="subtitle">Configure SMTP settings for sending reminder emails.</p>

          {emailLoading ? (
            <div className="loading">Loading...</div>
          ) : (
            <form onSubmit={handleSaveEmailConfig} className="email-config-form">
              <div className="form-row">
                <div className="form-group">
                  <label>SMTP Host *</label>
                  <input
                    type="text"
                    value={emailConfig.host}
                    onChange={(e) => handleEmailConfigChange('host', e.target.value)}
                    required
                    placeholder="smtp.gmail.com"
                  />
                </div>
                <div className="form-group">
                  <label>SMTP Port *</label>
                  <input
                    type="number"
                    value={emailConfig.port}
                    onChange={(e) => handleEmailConfigChange('port', e.target.value)}
                    required
                    placeholder="587"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Email User/Username *</label>
                  <input
                    type="email"
                    value={emailConfig.user}
                    onChange={(e) => handleEmailConfigChange('user', e.target.value)}
                    required
                    placeholder="your_email@gmail.com"
                  />
                </div>
                <div className="form-group">
                  <label>Email Password/App Password *</label>
                  <input
                    type="password"
                    value={emailConfig.password}
                    onChange={(e) => handleEmailConfigChange('password', e.target.value)}
                    required={!emailConfig.id}
                    placeholder="Enter password or app password"
                  />
                  <small>For Gmail, use an App Password instead of your regular password</small>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>From Email Address *</label>
                  <input
                    type="email"
                    value={emailConfig.from}
                    onChange={(e) => handleEmailConfigChange('from', e.target.value)}
                    required
                    placeholder="noreply@teatime.com"
                  />
                </div>
                <div className="form-group">
                  <label>Secure (TLS/SSL)</label>
                  <select
                    value={emailConfig.secure ? 'true' : 'false'}
                    onChange={(e) => handleEmailConfigChange('secure', e.target.value)}
                  >
                    <option value="false">No (STARTTLS)</option>
                    <option value="true">Yes (SSL/TLS)</option>
                  </select>
                </div>
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={emailConfig.isActive}
                    onChange={(e) => handleEmailConfigChange('isActive', e.target.checked)}
                  />
                  <span>Enable email sending</span>
                </label>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary" disabled={savingEmail}>
                  {savingEmail ? 'Saving...' : 'Save Configuration'}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleTestEmail}
                  disabled={testingEmail || !emailConfig.isActive}
                >
                  {testingEmail ? 'Sending...' : 'Send Test Email'}
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="backup-section">
          <h2>Data Backup</h2>
          <p className="subtitle">Create and manage backups of database and uploaded files.</p>

          <div className="backup-config-section">
            <h3>Backup Schedule Configuration</h3>
            {backupConfigLoading ? (
              <div className="loading">Loading configuration...</div>
            ) : (
              <form onSubmit={handleSaveBackupConfig} className="backup-config-form">
                <div className="schedule-selector">
                  <h4>Backup Schedule</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Frequency *</label>
                      <select
                        value={scheduleUI.frequency}
                        onChange={(e) => handleScheduleUIChange('frequency', e.target.value)}
                        required
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Time *</label>
                      <div className="time-input-group">
                        <select
                          value={scheduleUI.hour}
                          onChange={(e) => handleScheduleUIChange('hour', e.target.value)}
                          required
                        >
                          {Array.from({ length: 24 }, (_, i) => (
                            <option key={i} value={i}>
                              {i.toString().padStart(2, '0')}:00
                            </option>
                          ))}
                        </select>
                        <span>:</span>
                        <select
                          value={scheduleUI.minute}
                          onChange={(e) => handleScheduleUIChange('minute', e.target.value)}
                          required
                        >
                          {[0, 15, 30, 45].map(m => (
                            <option key={m} value={m}>
                              {m.toString().padStart(2, '0')}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  {scheduleUI.frequency === 'weekly' && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Day of Week *</label>
                        <select
                          value={scheduleUI.dayOfWeek}
                          onChange={(e) => handleScheduleUIChange('dayOfWeek', e.target.value)}
                          required
                        >
                          <option value="0">Sunday</option>
                          <option value="1">Monday</option>
                          <option value="2">Tuesday</option>
                          <option value="3">Wednesday</option>
                          <option value="4">Thursday</option>
                          <option value="5">Friday</option>
                          <option value="6">Saturday</option>
                        </select>
                      </div>
                    </div>
                  )}
                  {scheduleUI.frequency === 'monthly' && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Day of Month *</label>
                        <select
                          value={scheduleUI.dayOfMonth}
                          onChange={(e) => handleScheduleUIChange('dayOfMonth', e.target.value)}
                          required
                        >
                          {Array.from({ length: 31 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {i + 1}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                  <div className="schedule-preview">
                    <strong>Schedule Preview:</strong> {backupConfig.schedule}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Timezone *</label>
                    <select
                      value={backupConfig.timezone}
                      onChange={(e) => handleBackupConfigChange('timezone', e.target.value)}
                      required
                    >
                      <option value="America/New_York">Eastern Time (EST/EDT)</option>
                      <option value="America/Chicago">Central Time (CST/CDT)</option>
                      <option value="America/Denver">Mountain Time (MST/MDT)</option>
                      <option value="America/Los_Angeles">Pacific Time (PST/PDT)</option>
                      <option value="UTC">UTC</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Keep Last N Backups</label>
                    <input
                      type="number"
                      value={backupConfig.keepCount}
                      onChange={(e) => handleBackupConfigChange('keepCount', e.target.value)}
                      min="1"
                      max="100"
                      required
                    />
                    <small>Number of automatic backups to keep (old ones will be deleted)</small>
                  </div>
                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={backupConfig.isEnabled}
                        onChange={(e) => handleBackupConfigChange('isEnabled', e.target.checked)}
                      />
                      <span>Enable automatic backups</span>
                    </label>
                  </div>
                </div>
                {backupConfig.lastBackupAt && (
                  <div className="backup-last-run">
                    <strong>Last backup:</strong> {moment(backupConfig.lastBackupAt).format('MMM DD, YYYY hh:mm A')}
                  </div>
                )}
                <div className="form-actions">
                  <button type="submit" className="btn-primary" disabled={savingBackupConfig}>
                    {savingBackupConfig ? 'Saving...' : 'Save Configuration'}
                  </button>
                </div>
              </form>
            )}
          </div>

          <div className="backup-actions">
            <div className="backup-action-buttons">
              <button
                className="btn-primary"
                onClick={handleCreateBackup}
                disabled={creatingBackup || restoringFromFile}
              >
                {creatingBackup ? 'Creating Backup...' : '📦 Create Backup Now'}
              </button>
              <input
                type="file"
                id="restoreFileInput"
                accept=".zip"
                style={{ display: 'none' }}
                onChange={handleRestoreFromFile}
              />
              <button
                className="btn-primary"
                onClick={() => document.getElementById('restoreFileInput').click()}
                disabled={restoringFromFile || creatingBackup}
              >
                {restoringFromFile ? 'Restoring...' : '↶ Restore with File'}
              </button>
            </div>
            <p className="backup-info">
              {backupConfig.isEnabled 
                ? `Automatic backups are scheduled: ${backupConfig.schedule} (${backupConfig.timezone}). Manual backups can be created anytime.`
                : 'Automatic backups are disabled. Manual backups can be created anytime.'}
            </p>
          </div>

          {backupLoading ? (
            <div className="loading">Loading backups...</div>
          ) : (
            <div className="backups-list">
              {backups.length === 0 ? (
                <div className="no-backups">No backups found</div>
              ) : (
                <table className="backups-table">
                  <thead>
                    <tr>
                      <th>File Name</th>
                      <th>Size</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backups.map((backup, index) => (
                      <tr key={index}>
                        <td>{backup.fileName}</td>
                        <td>{backup.sizeFormatted}</td>
                        <td>{moment(backup.createdAt).format('MMM DD, YYYY hh:mm A')}</td>
                        <td>
                          <div className="backup-actions-cell">
                            <button
                              className="btn-restore"
                              onClick={() => handleRestoreBackup(backup.fileName)}
                              title="Restore from this backup"
                            >
                              ↶ Restore
                            </button>
                            <button
                              className="btn-download"
                              onClick={() => handleDownloadBackup(backup.fileName)}
                              title="Download Backup"
                            >
                              📥 Download
                            </button>
                            <button
                              className="btn-send-email"
                              onClick={() => handleOpenSendEmailModal(backup.fileName)}
                              title="Send backup via email"
                            >
                              ✉️ Send Email
                            </button>
                            <button
                              className="btn-delete"
                              onClick={() => handleDeleteBackup(backup.fileName)}
                              title="Delete Backup"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {/* <div className="pto-management-section">
          <h2>PTO Requests Management</h2>
          <p className="subtitle">Review and manage associate PTO requests for {moment().year()}.</p>

          <div className="filters" style={{ marginBottom: '20px' }}>
            <label style={{ marginRight: '10px' }}>Filter by User:</label>
            <select 
              value={selectedUser} 
              onChange={(e) => setSelectedUser(e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="all">All Users</option>
              {users.filter(u => u.role === 'associate').map(user => (
                <option key={user.id} value={user.id}>{user.name}</option>
              ))}
            </select>
          </div>

          {ptoLoading ? (
            <div className="loading">Loading PTO requests...</div>
          ) : (
            <div className="pto-table-wrapper">
              <table className="pto-admin-table">
                <thead>
                  <tr>
                    <th>Associate</th>
                    <th>Date</th>
                    <th>Hours</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Reason</th>
                    <th>Requested By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ptoRequests
                    .filter(pto => selectedUser === 'all' || pto.userId === selectedUser)
                    .sort((a, b) => moment(b.date).diff(moment(a.date)))
                    .map((pto) => (
                      <tr key={pto.id}>
                        <td>{pto.user?.name || 'Unknown'}</td>
                        <td>{moment(pto.date).format('MMM DD, YYYY')}</td>
                        <td>{pto.hours}</td>
                        <td>
                          <span className={`pto-type-badge ${pto.type}`}>
                            {pto.type}
                          </span>
                        </td>
                        <td>
                          <span className={`pto-status-badge ${pto.status}`}>
                            {pto.status}
                          </span>
                        </td>
                        <td>{pto.reason || '-'}</td>
                        <td>{pto.requester?.name || '-'}</td>
                        <td>
                          <div className="pto-actions">
                            {pto.status === 'pending' && (
                              <>
                                <button
                                  className="btn-approve"
                                  onClick={() => handlePTOStatusChange(pto.id, 'approved')}
                                  title="Approve"
                                >
                                  ✓
                                </button>
                                <button
                                  className="btn-reject"
                                  onClick={() => handlePTOStatusChange(pto.id, 'rejected')}
                                  title="Reject"
                                >
                                  ✗
                                </button>
                              </>
                            )}
                            {pto.status === 'approved' && (
                              <button
                                className="btn-reject"
                                onClick={() => handlePTOStatusChange(pto.id, 'rejected')}
                                title="Reject"
                              >
                                ✗ Reject
                              </button>
                            )}
                            {pto.status === 'rejected' && (
                              <button
                                className="btn-approve"
                                onClick={() => handlePTOStatusChange(pto.id, 'approved')}
                                title="Approve"
                              >
                                ✓ Approve
                              </button>
                            )}
                            {canDeletePTO(pto) && (
                              <button
                                className="btn-delete-small"
                                onClick={() => handlePTODelete(pto.id)}
                                title="Delete"
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  {ptoRequests.filter(pto => selectedUser === 'all' || pto.userId === selectedUser).length === 0 && (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                        No PTO requests found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div> */}

        <div className="reminders-section">
          <h2>Reminder Configuration</h2>
          <p className="subtitle">Configure and manage time entry reminders for associates.</p>

          {loading ? (
            <div className="loading">Loading...</div>
          ) : (
            <div className="reminders-grid">
              {reminders.map((reminder) => (
                <div key={reminder.id} className="reminder-card">
                  <div className="reminder-header">
                    <h3>{reminder.name}</h3>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={reminder.isEnabled}
                        onChange={() => handleToggle(reminder.id, reminder.isEnabled)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  <p className="reminder-description">{reminder.description}</p>
                  <div className="reminder-schedule">
                    <label>Schedule:</label>
                    <div className="schedule-inputs">
                      <select 
                        className="schedule-select" 
                        value={reminder.schedule?.day || ''}
                        onChange={(e) => handleScheduleUpdate(reminder.id, 'day', e.target.value)}
                      >
                        <option value="monday">Monday</option>
                        <option value="tuesday">Tuesday</option>
                        <option value="wednesday">Wednesday</option>
                        <option value="thursday">Thursday</option>
                        <option value="friday">Friday</option>
                        <option value="saturday">Saturday</option>
                        <option value="sunday">Sunday</option>
                      </select>
                      <input 
                        type="time" 
                        className="schedule-time"
                        value={reminder.schedule?.time || '00:00'}
                        onChange={(e) => handleScheduleUpdate(reminder.id, 'time', e.target.value)}
                      />
                    </div>
                  </div>
                  <button
                    className="trigger-button"
                    onClick={() => handleTrigger(reminder.id, reminder.type)}
                  >
                    🔔 Trigger Now
                  </button>
                  {reminder.lastTriggered && (
                    <p className="last-triggered">
                      Last triggered: {moment(reminder.lastTriggered).format('MMM DD, YYYY hh:mm A')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Send Backup Email Modal */}
      {sendEmailModal.show && (
        <div className="modal-overlay" onClick={handleCloseSendEmailModal}>
          <div className="modal-content send-email-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Send Backup via Email</h3>
              <button type="button" className="modal-close" onClick={handleCloseSendEmailModal} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSendBackupEmail}>
              <div className="modal-body">
                <p className="send-email-file-name">File: <strong>{sendEmailModal.fileName}</strong></p>
                <div className="form-group">
                  <label htmlFor="send-email-to">To (email address) *</label>
                  <input
                    id="send-email-to"
                    type="email"
                    value={sendEmailModal.to}
                    onChange={(e) => setSendEmailModal(prev => ({ ...prev, to: e.target.value }))}
                    placeholder="recipient@example.com"
                    required
                  />
                </div>
                <p className="send-email-note">Uses the email configuration from Admin Dashboard.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={handleCloseSendEmailModal} disabled={sendingBackupEmail}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={sendingBackupEmail}>
                  {sendingBackupEmail ? 'Sending...' : '✉️ Send Email'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Admin;

